'use client';

import { useEffect } from 'react';

import { performTokenRefresh } from '@/infrastructure/http/authRefresh';
import { authStore } from '@/infrastructure/store/authStore';

/**
 * Embeds the Brainwave widget via a single <script> tag — the same mechanism
 * locally and in production. The bundle is SELF-GATING, so this mounts outside
 * any auth guard: the widget UI only activates while `window.__MEGABRAIN_TOKEN__`
 * (wired BEFORE the bundle is injected) returns a token, and on Brainwave
 * preview hosts the same bundle runs only the click-to-target companion +
 * keep-alive — which must work even on public, logged-out pages.
 *
 * The bundle URL defaults to the deployed Brainwave, so the only per-app env
 * required is NEXT_PUBLIC_MEGABRAIN_PROJECT_ID. Set NEXT_PUBLIC_MEGABRAIN_URL to
 * override it (e.g. http://localhost:4317/megabrain.js for local widget dev).
 */
const DEFAULT_BUNDLE_URL = 'https://brainwave-api.umode.app/megabrain.js';
const BUNDLE_URL = process.env.NEXT_PUBLIC_MEGABRAIN_URL?.trim() || DEFAULT_BUNDLE_URL;
const API_BASE = process.env.NEXT_PUBLIC_MEGABRAIN_API_BASE ?? '';
const PROJECT_ID = process.env.NEXT_PUBLIC_MEGABRAIN_PROJECT_ID ?? '';
const WIDGET_LANG = 'pt';
const SCRIPT_ID = 'megabrain-widget';

/**
 * Env PUBLICA que o preview da sessao HERDA de quem o abriu.
 *
 * O preview roda noutro host (<sessao>.brainwave.umode.app) e pega a sessao do
 * usuario pelo cookie SSO compartilhado em .umode.app. So que o nome desse
 * cookie carrega o ambiente (`umode_session_<env>`): um preview fixado em
 * "staging", aberto a partir de PRODUCAO, procura um cookie que nao existe.
 * Sem token, toda chamada ao backend sai sem Authorization e volta 401. Herdar
 * o ambiente do host mata essa classe de erro e ainda aponta o preview para o
 * gateway certo.
 */
function buildPreviewEnv(): string {
  const inherited: Record<string, string> = {};
  const carry = (key: string, value: string | undefined): void => {
    if (typeof value === 'string' && value.trim() !== '') {
      inherited[key] = value.trim();
    }
  };
  carry('NEXT_PUBLIC_APP_ENV', process.env.NEXT_PUBLIC_APP_ENV);
  carry('NEXT_PUBLIC_BASE_API_GATEWAY_URL', process.env.NEXT_PUBLIC_BASE_API_GATEWAY_URL);
  carry('NEXT_PUBLIC_DASHBOARD_URL', process.env.NEXT_PUBLIC_DASHBOARD_URL);
  // O appId completa o link de login que o preview oferece a quem abre o
  // link compartilhado sem sessao da casa.
  carry('NEXT_PUBLIC_APP_ID', process.env.NEXT_PUBLIC_APP_ID);
  return Object.keys(inherited).length > 0 ? JSON.stringify(inherited) : '';
}

interface MegaBrainGlobals {
  __MEGABRAIN_TOKEN__?: () => string | null;
  __MEGABRAIN_REFRESH_TOKEN__?: () => Promise<string | null>;
}

export function MegaBrainEmbed() {
  useEffect(() => {
    const target = window as unknown as MegaBrainGlobals;

    // Wire the token provider BEFORE injecting the bundle so the widget reads the
    // current access token on first run.
    target.__MEGABRAIN_TOKEN__ = () => authStore.getState().accessToken;

    // Wire the refresh hook so the widget can renew an EXPIRED access token on a
    // 401 instead of erroring on every prompt. Without this a long session
    // (token aged out) 401s forever, even on a new chat. Shares the app's own
    // single-flight refresh, so the rotating refresh token is never raced.
    target.__MEGABRAIN_REFRESH_TOKEN__ = () => performTokenRefresh();

    // Inject the widget bundle once.
    if (document.getElementById(SCRIPT_ID) === null) {
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = BUNDLE_URL;
      script.async = true;
      if (API_BASE !== '') {
        script.setAttribute('data-api-base', API_BASE);
      }
      if (PROJECT_ID !== '') {
        script.setAttribute('data-project-id', PROJECT_ID);
      }
      const previewEnv = buildPreviewEnv();
      if (previewEnv !== '') {
        script.setAttribute('data-preview-env', previewEnv);
      }
      script.setAttribute('data-lang', WIDGET_LANG);
      document.body.appendChild(script);
    }

    // On unmount, stop handing out the token so the already-loaded widget
    // self-hides.
    return () => {
      delete target.__MEGABRAIN_TOKEN__;
      delete target.__MEGABRAIN_REFRESH_TOKEN__;
    };
  }, []);

  return null;
}
